import { useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import type { Category } from '../categories/categoryTree'
import type { Account } from '../accounts/useAccounts'
import { useParams } from 'react-router'
import { AlertTriangle } from 'lucide-react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import { AlertPanel, Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { accountLabel } from '../accounts/accountLabel'
import { useAccounts } from '../accounts/useAccounts'
import { useCategories } from '../categories/useCategories'
import { LifecycleConfirm, type PendingConfirm } from './BillLifecycle'
import { BillSettings } from './BillSettings'
import { ChoosePaymentSheet } from './ChoosePaymentSheet'
import { detailStats, heroStatus, type DetailStats } from './detailStats'
import { PaymentHistory, type FindPast } from './PaymentHistory'
import { PeriodSheet } from './PeriodSheet'
import { ReconnectBanner } from './ReconnectBanner'
import { RecurringBackLink, RemovedBill } from './BillChrome'
import { recurringEditPath } from './recurringPaths'
import { canMarkPaid, canSkip, localToday, type LocalDate } from './recurringRules'
import { frequencyLabel, type Bill, type Entry, type Period } from './recurringWire'
import { useBill, usePeriods, type PeriodsResult } from './useRecurring'
import { PeriodLinkError, FIND_FAILED, findPastCopy, usePeriodLinks } from './usePeriodLinks'
import './recurring.css'

const LOAD_ERROR = 'This recurring payment could not be loaded.'

/** S11, verbatim. */
const RECONNECT_COPY = {
  reauth_required: "Your bank needs you to sign in again. Until then, payments can't be confirmed.",
  disconnected: "This account is disconnected. Payments can't be confirmed until you reconnect it.",
} as const

type DetailData = { bill: Bill } & PeriodsResult

/**
 * The bill and its timeline as one state: either failing is the screen's one
 * error sentence with one Try again for whichever failed. A removed bill never
 * reaches here — the screen shows its own state for that first.
 */
export function combineDetail(bill: AsyncState<Bill>, periods: AsyncState<PeriodsResult>): AsyncState<DetailData> {
  if (bill.status === 'error' || periods.status === 'error') {
    const recoveries = [bill, periods].flatMap((s) => (s.status === 'error' && s.recovery ? [s.recovery] : []))
    return {
      status: 'error',
      message: LOAD_ERROR,
      ...(recoveries.length > 0 ? { recovery: { label: 'Try again', onAct: () => recoveries.forEach((r) => r.onAct()) } } : {}),
    }
  }
  if (bill.status === 'loading' || periods.status === 'loading' || bill.status === 'empty' || periods.status === 'empty') {
    return { status: 'loading' }
  }
  const data = { bill: bill.data, ...periods.data }
  // Neither hook passes an `asOf`, so stale cannot arrive; if it ever did, Async refuses it on a live surface (P1).
  if (bill.status === 'stale') return { status: 'stale', data, asOf: bill.asOf }
  if (periods.status === 'stale') return { status: 'stale', data, asOf: periods.asOf }
  return { status: 'success', data }
}

// -------------------------------------------------------------------- stats

type TileValue = { shown: ReactNode; spoken?: string }

/** "—" is drawn; its meaning is spoken (shape §4.7). Never a fabricated 0. */
const dash = (spoken: string): TileValue => ({ shown: '—', spoken })

function StatTile({ label, value, empty }: { label: string; value: TileValue; empty: boolean }) {
  const labelId = useId()
  const valueId = useId()
  return (
    <Panel role="group" aria-labelledby={`${labelId} ${valueId}`} className="rec-tile rec-stat">
      <span id={labelId} className="rec-tile__label">
        {label}
      </span>
      <span id={valueId} className={`rec-stat__value${empty ? ' rec-stat__value--empty' : ''}`}>
        <span aria-hidden={value.spoken !== undefined || undefined}>{value.shown}</span>
        {value.spoken !== undefined && <span className="sr-only">{value.spoken}</span>}
      </span>
    </Panel>
  )
}

/**
 * The four design tiles (D12, R11). `row` is 4-up; `grid` is 2 × 2, for the
 * widths where four would leave "11 of 13" about 70px (shape O4).
 */
export function StatTiles({ stats, layout }: { stats: DetailStats; layout: 'row' | 'grid' }) {
  const { paid12m, average, onTime, trend } = stats
  const paid: TileValue =
    paid12m.length === 0
      ? dash('none')
      : {
          shown: paid12m.map((t) => (
            <span key={t.currency} className="rec-stat__line">
              <Money cents={t.cents} currency={t.currency} />
            </span>
          )),
        }
  const avg: TileValue =
    average !== null
      ? { shown: <Money cents={average.cents} currency={average.currency} /> }
      : dash(paid12m.length > 1 ? 'mixed currencies' : 'not enough payments')
  const time: TileValue = onTime.settled === 0 ? dash('no settled payments') : { shown: `${onTime.onTime} of ${onTime.settled}` }
  // D12: neutral ink, the glyph AND the word — never colour alone.
  const change: TileValue =
    trend === null
      ? dash('No change')
      : {
          shown: (
            <>
              <span aria-hidden="true">{trend.direction === 'up' ? '▲' : '▼'}</span>
              <span className="sr-only">{trend.direction}</span> {trend.percent}%
            </>
          ),
        }
  return (
    <div className={`rec-stats rec-stats--${layout}`}>
      <StatTile label="Paid in 12 months" value={paid} empty={paid12m.length === 0} />
      <StatTile label="Average" value={avg} empty={average === null} />
      <StatTile label="On time" value={time} empty={onTime.settled === 0} />
      <StatTile label="Trend" value={change} empty={trend === null} />
    </div>
  )
}

// --------------------------------------------------------------------- hero

/** Data from either lookup, whatever its freshness; nothing while it loads or after it fails. */
const dataOf = <T,>(s: AsyncState<T[]>): T[] => (s.status === 'success' || s.status === 'stale' ? s.data : [])

/**
 * "Monthly · Housing · Chequing ••4821" (R10). Both lookups are treated alike:
 * success or stale supplies a name, and a lookup that is loading, failed, or
 * does not know the id leaves its part out — never a gap, never an error.
 * D4: no owner, although the account carries one.
 */
export function billMeta(bill: Bill, accounts: AsyncState<Account[]>, categories: AsyncState<Category[]>): string {
  const account = bill.accountId === null ? undefined : dataOf(accounts).find((a) => a.id === bill.accountId)
  const category = bill.categoryId === null ? undefined : dataOf(categories).find((c) => c.id === bill.categoryId)
  return [frequencyLabel(bill.frequency), category?.name, account ? accountLabel(account) : undefined]
    .filter((s): s is string => s !== undefined)
    .join(' · ')
}

/** Presentational: the screen fetches, the hero only renders what it is given. */
function Hero({
  bill,
  periods,
  meta,
  today,
  compact,
  onMarkPaid,
  onSkip,
  onSettings,
  settingsRef,
}: {
  bill: Bill
  periods: Period[]
  meta: string
  today: LocalDate
  compact: boolean
  onMarkPaid: (() => void) | null
  onSkip: (() => void) | null
  onSettings: () => void
  settingsRef: RefObject<HTMLButtonElement | null>
}) {
  const status = heroStatus(bill, periods, today)
  const overdue = status.kind === 'overdue'
  const body = (
    <>
      <div className="rec-hero__id">
        <MerchantAvatar
          merchant={{ id: bill.id, name: bill.merchant || bill.name, type: 'other', icon: bill.icon, icon_url: bill.iconUrl, is_global: false }}
          size={compact ? 'row' : 'detail'}
        />
        <div className="rec-hero__text">
          {/* The bill IS the page's subject: an h2 under the TopBar's h1, as TransactionDetail titles its merchant (Ruling 31). */}
          <h2 className="rec-hero__name">{bill.name}</h2>
          <p className={`rec-hero__status${overdue ? ' rec-hero__status--overdue' : ''}`}>
            {overdue && <Icon glyph={AlertTriangle} size={13} />}
            {status.text}
          </p>
          <p className="rec-hero__meta">{meta}</p>
        </div>
      </div>
      <div className="rec-hero__side">
        <Money cents={bill.expectedCents} currency={bill.currency} variant="hero" kind={bill.isInflow ? 'flow' : 'stock'} />
        <div className="rec-hero__actions">
          {/* Ruling 34: Mark paid only for the period the server pays; the hero offers Skip on that same next period (#809). Edit and Bill settings always. */}
          {onMarkPaid && (
            <Button variant="primary" size="panel" onClick={onMarkPaid}>
              Mark paid
            </Button>
          )}
          {onSkip && (
            <Button variant="outline" size="panel" onClick={onSkip}>
              Skip this one
            </Button>
          )}
          <ButtonLink to={recurringEditPath(bill.id)} variant="outline" size="panel">
            Edit
          </ButtonLink>
          <Button ref={settingsRef} variant="outline" size="panel" onClick={onSettings}>
            Bill settings
          </Button>
        </div>
      </div>
    </>
  )
  const className = `rec-hero${compact ? ' rec-hero--compact' : ''}`
  // The alert skin means "this bill is overdue": anything else is a plain tile.
  return overdue ? <AlertPanel className={className}>{body}</AlertPanel> : <Panel className={className}>{body}</Panel>
}

// ------------------------------------------------------------------- states

function DetailSkeleton({ compact, statsLayout }: { compact: boolean; statsLayout: 'row' | 'grid' }) {
  return (
    <div className="rec-body">
      {/* Plain Panel: the overdue skin is unknown until load, and colour must not guess. */}
      <Panel className={`rec-hero${compact ? ' rec-hero--compact' : ''}`}>
        <div className="rec-hero__id">
          <Skeleton height={compact ? 34 : 52} width={compact ? 34 : 52} />
          <div className="rec-hero__text rec-hero__text--skeleton">
            <Skeleton height={22} width="30%" />
            <Skeleton height={13} width="40%" />
            <Skeleton height={12} width="35%" />
          </div>
        </div>
        <div className="rec-hero__side">
          <Skeleton height={34} width={140} />
        </div>
      </Panel>
      <div className={`rec-stats rec-stats--${statsLayout}`}>
        {[0, 1, 2, 3].map((i) => (
          <Panel key={i} className="rec-tile rec-stat">
            <Skeleton height={10} width="50%" />
            <Skeleton height={24} width="45%" />
          </Panel>
        ))}
      </div>
      <Panel className="rec-hist">
        <div className="rec-hist__head">
          <Skeleton height={15} width={130} />
          <Skeleton height={11} width={120} />
        </div>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="rec-skeleton-row rec-hist__skeleton-row">
            <Skeleton height={13} width="25%" />
            <Skeleton height={13} width={70} />
          </div>
        ))}
      </Panel>
    </div>
  )
}

// ---------------------------------------------------------- find past payments

/**
 * "Find past payments" (Task 10, `POST match-history`): offered only when the
 * bill has an account — with none the server can only refuse (422), so there
 * is no button to refuse. The outcome stays under the history heading until
 * the next attempt: "Linked N past payments", "No past payments found", or
 * why it could not run.
 */
function useFindPast(bill: Bill): FindPast | undefined {
  const { findPast, busy } = usePeriodLinks(bill)
  const [outcome, setOutcome] = useState<FindPast['outcome']>(null)
  if (bill.accountId === null) return undefined
  return {
    busy,
    outcome,
    onFind: () => {
      setOutcome(null)
      findPast().then(
        (n) => setOutcome({ tone: 'done', text: findPastCopy(n) }),
        (e: unknown) => setOutcome({ tone: 'problem', text: e instanceof PeriodLinkError ? e.message : FIND_FAILED }),
      )
    },
  }
}

function History({
  bill,
  periods,
  truncated,
  today,
  phone,
}: {
  bill: Bill
  periods: Period[]
  truncated: boolean
  today: LocalDate
  phone: boolean
}) {
  const findPast = useFindPast(bill)
  // The due date, not the row: the sheet follows the live timeline entry.
  const [opened, setOpened] = useState<Period | null>(null)
  const live = opened === null ? undefined : (periods.find((p) => p.dueDate === opened.dueDate) ?? opened)
  return (
    <>
      <PaymentHistory bill={bill} periods={periods} truncated={truncated} today={today} phone={phone} onOpen={setOpened} findPast={findPast} />
      {live && <PeriodSheet bill={bill} period={live} today={today} onClose={() => setOpened(null)} />}
    </>
  )
}

// ------------------------------------------------------------------- screen

const entryOf = (bill: Bill, p: Period): Entry => ({
  bill,
  dueDate: p.dueDate,
  status: p.status,
  payment: p.payment,
  expectedCents: bill.expectedCents,
})

/**
 * `/recurring/:id` (M7-R10–R13, R26). The back link, the hero, S11's note
 * when the bank needs reconnecting, the four stat tiles, and the payment
 * history. `live` (P1): four states, plus the removed state for `is_removed`
 * or a 404, which replaces everything below the back link.
 */
export function RecurringDetail() {
  const { id = '' } = useParams()
  // One "today" per mount, so the periods query key and every relative date agree.
  const [today] = useState(() => localToday())
  const bp = useBreakpoint()
  const phone = bp === 'phone'
  const statsLayout = phone || bp === 'tablet' ? 'grid' : 'row'
  const bill = useBill(id)
  const periods = usePeriods(id, today)
  // M5's and M11's exported hooks, read here so the hero stays presentational.
  const accounts = useAccounts()
  const categories = useCategories()
  const [paying, setPaying] = useState(false)
  const [confirming, setConfirming] = useState<PendingConfirm | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsRef = useRef<HTMLButtonElement>(null)
  const state = combineDetail(bill, periods)
  // Set once the bill has been on screen, so a removal mid-session moves focus.
  // Set during render (React's derived-state pattern): it never goes back.
  const [seen, setSeen] = useState(false)
  if (state.status === 'success' && !seen) setSeen(true)

  return (
    <div className="rec-page rec-detail">
      <RecurringBackLink />
      {bill.removed ? (
        <RemovedBill focus={seen} />
      ) : (
        <Async
          surface="recurring-detail"
          state={state}
          skeleton={<DetailSkeleton compact={phone} statsLayout={statsLayout} />}
          // Unreachable: `combineDetail` never yields empty. Required by Async.
          empty={{ title: 'This bill was removed' }}
        >
          {(d) => {
            const next = d.bill.nextDue === null ? undefined : d.periods.find((p) => p.dueDate === d.bill.nextDue)
            const nextEntry = next ? entryOf(d.bill, next) : undefined
            const reconnect =
              d.bill.accountStatus === 'reauth_required' || d.bill.accountStatus === 'disconnected'
                ? RECONNECT_COPY[d.bill.accountStatus]
                : null
            return (
              <div className="rec-body">
                <Hero
                  bill={d.bill}
                  periods={d.periods}
                  meta={billMeta(d.bill, accounts, categories)}
                  today={today}
                  compact={phone}
                  onMarkPaid={canMarkPaid(d.bill, nextEntry) ? () => setPaying(true) : null}
                  onSkip={nextEntry && canSkip(d.bill, nextEntry) ? () => setConfirming({ kind: 'skip', entry: nextEntry }) : null}
                  onSettings={() => setSettingsOpen(true)}
                  settingsRef={settingsRef}
                />
                {reconnect && <ReconnectBanner text={reconnect} />}
                <StatTiles stats={detailStats(d.periods, d.bill.currency, today)} layout={statsLayout} />
                <History bill={d.bill} periods={d.periods} truncated={d.truncated} today={today} phone={phone} />
                {paying && <ChoosePaymentSheet bill={d.bill} onClose={() => setPaying(false)} />}
                {settingsOpen && (
                  <BillSettings bill={d.bill} onClose={() => setSettingsOpen(false)} restoreFocusTo={settingsRef} />
                )}
              </div>
            )
          }}
        </Async>
      )}
      {confirming && <LifecycleConfirm confirm={confirming} onClose={() => setConfirming(null)} />}
    </div>
  )
}
